import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useProjectMessages } from '../chat/useProjectMessages'
import { useQuery } from '@tanstack/react-query'
import { fetchSession } from '../auth/googleSession'
import { Message } from '../chat/ProjectPanel'
import { projectEcho } from '../chat/projectEcho'
import { sendProjectMessage, type ProjectMessage } from '../transport/api'
import { Icon } from '../ui/Icon'
import type { OpenDoc } from '../files/openDoc'
import { AgentMention, findMention, pingTag, type MentionAgent } from '../agents/AgentMention'

/** Where the window is: a board of the room, or one of its pages (owner, 2026-11-02 — the same window over a page). */
export type ChatFrom = { board: string; name: string } | { page: string; name: string }

/**
 * THE PROJECT'S CHAT, OVER ITS WHITEBOARD (owner, 2026-10-30: 在 Board 的右下方新增一個 Chat icon. 使用者點擊之後, 可以彈出
 * 背景半透明的小型聊天視窗, 呈現 Project Chat 裡的聊天內容. 但是使用該聊天視窗發出信的時候, 會有該 whiteboard 的標籤送出).
 *
 * The room's own messages, drawn as Project Chat draws them (chat/ProjectPanel Message), in a small see-through
 * window over the board. What is sent from here names this board (chat messages.ts BoardFields): Project Chat shows
 * the board on the message, and an agent fed it is told which board, and how to read it.
 *
 * The messages come from the board screen's one subscription (useProjectMessages), so the button's count of new
 * messages and this list are the same list.
 *
 * `@` pings one of the room's agents (owner, 2026-10-30: 讓右邊彈出的 Message window 可以使用 @ 來 ping project chat 裡的
 * agent) — the composer's own picker (agents/AgentMention) and its tag, so the message reads in Project Chat exactly
 * as one written there.
 *
 * And over a PAGE (owner, 2026-11-02: 還能夠跟 Agents 共同編輯, 以及與朋友在 Project Chat 裡面協作): the same window, and
 * what is sent from it names that page (chat messages.ts PageFields) — Project Chat shows the page on the message and
 * opens it, an agent is told how to read it.
 */

/** A document a message opens (a file, a YouTube link): its own tab — the cover panel is under the board. */
const openOutside: OpenDoc = (d) => {
  window.open(d.url, '_blank', 'noopener')
}

/** The box grows a line at a time with what is typed, to at most this many lines; past it, it scrolls. */
const MAX_LINES = 5

/**
 * The box's height for its content (owner, 2026-10-30: 文字輸入欄位會隨著換行而增高. 最高增加至 5 行): what the text needs
 * (`scrollHeight`, padding included), at least one line and at most MAX_LINES, plus the border; and whether the rest
 * must scroll.
 */
export function boxHeight(scrollHeight: number, line: number, padding: number, border: number): { height: number; scroll: boolean } {
  const min = line + padding
  const max = line * MAX_LINES + padding
  return { height: Math.max(min, Math.min(scrollHeight, max)) + border, scroll: scrollHeight > max + 1 }
}

/** Enter sends; Shift+Enter is a new line; Enter that ends an input method's composition (Chinese, Japanese) is not a send. */
export function isSendKey(e: { key: string; shiftKey: boolean; isComposing?: boolean; keyCode?: number }): boolean {
  return e.key === 'Enter' && !e.shiftKey && !e.isComposing && e.keyCode !== 229
}

/** A quote longer than this is cut: the agent is told which page it is on and reads the rest there. */
export const QUOTE_MAX = 1500

/**
 * A question about a passage of a page (owner, 2026-11-03: 反白選擇一段 page 裡面的文字 … 傳信息給 ping 的 Agent 讓他依照需求來
 * 修改內容. 或是回答問題): the passage as a Markdown quote, then the words. So the room reads what was asked about, and an
 * agent gets it in the message itself, with the page named beside it (chat streamFeed's ▤ line).
 */
export function quotedBody(quote: string, words: string): string {
  const text = quote.trim()
  const cut = [...text].length > QUOTE_MAX ? `${[...text].slice(0, QUOTE_MAX).join('').trimEnd()}…` : text
  return `${cut.split(/\r?\n/).map((l) => (l.trim() ? `> ${l}` : '>')).join('\n')}\n\n${words}`
}

export function BoardChat({
  convId,
  from,
  project,
  agents,
  messages,
  loading,
  hasOlder,
  loadingOlder,
  loadOlder,
  onClose,
  quote,
  onQuoteDone,
}: {
  convId: string
  /** The board or page it is over: what is sent from here names it. */
  from: ChatFrom
  project: string
  /** The room's agents, for `@` (agents/AgentMention mentionRowsOfMembers). */
  agents: MentionAgent[]
  messages: ProjectMessage[]
  loading: boolean
  hasOlder: boolean
  loadingOlder: boolean
  loadOlder: () => void
  onClose: () => void
  /** What the person marked on the page and asked about: it goes above their words (`quotedBody`), then it is done. */
  quote?: string | null
  onQuoteDone?: () => void
}) {
  const me = useQuery({ queryKey: ['auth-me'], queryFn: fetchSession, staleTime: Infinity })
  const who = me.data?.kind === 'in' ? me.data.identity : null
  const [draft, setDraft] = useState('')
  const [sendError, setSendError] = useState<string | null>(null)
  /** Non-null while an `@mention` is being typed: where it starts and what is typed after it. */
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null)
  const syncMention = (el: HTMLTextAreaElement) => setMention(findMention(el.value, el.selectionStart ?? el.value.length))
  /** The `@query` under the caret becomes the agent's ping tag; the caret goes after it. */
  const insertMention = (a: MentionAgent) => {
    const el = box.current
    if (!el || !mention) return
    const before = draft.slice(0, mention.start)
    const after = draft.slice(mention.start + 1 + mention.query.length)
    const tag = pingTag(a)
    setDraft(`${before}${tag}${after}`)
    setMention(null)
    const caret = before.length + tag.length
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(caret, caret)
    })
  }
  /** The @ button (a phone has no easy @ key): an `@` at the caret, a word of its own, and the picker open. */
  const startMention = () => {
    const el = box.current
    if (!el) return
    const at = el.selectionStart ?? draft.length
    const lead = at > 0 && !/\s/.test(draft[at - 1] ?? '') ? ' @' : '@'
    const next = `${draft.slice(0, at)}${lead}${draft.slice(at)}`
    setDraft(next)
    const caret = at + lead.length
    setMention({ start: caret - 1, query: '' })
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(caret, caret)
    })
  }
  const list = useRef<HTMLDivElement>(null)
  const atBottom = useRef(true)
  const box = useRef<HTMLTextAreaElement>(null)

  // The newest message in view, as a chat does — unless the reader has scrolled up to read.
  useLayoutEffect(() => {
    const el = list.current
    if (el && atBottom.current) el.scrollTop = el.scrollHeight
  }, [messages.length])
  useEffect(() => {
    box.current?.focus()
  }, [])
  // The box is as tall as its lines, one to five (boxHeight), measured after every change — a wrapped line counts too.
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const cs = getComputedStyle(el)
    const line = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.35 || 21
    const padding = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0)
    const border = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0)
    el.style.height = 'auto'
    const { height, scroll } = boxHeight(el.scrollHeight, line, padding, border)
    el.style.height = `${height}px`
    el.style.overflowY = scroll ? 'auto' : 'hidden'
  }, [draft])

  // Asked about a passage: the words to write next are the question.
  useEffect(() => {
    if (quote) box.current?.focus()
  }, [quote])

  const send = async () => {
    const body = draft.trim()
    if (!body) return
    setDraft('')
    setMention(null)
    setSendError(null)
    atBottom.current = true
    try {
      const sent = await sendProjectMessage(
        convId,
        quote ? quotedBody(quote, body) : body,
        who?.email ?? null,
        who?.name ?? null,
        who?.picture ?? null,
        undefined,
        'board' in from ? { id: from.board, name: from.name } : undefined,
        'page' in from ? { id: from.page, name: from.name } : undefined,
      )
      if (sent.message) projectEcho(convId, sent.message as ProjectMessage)
      if (quote) onQuoteDone?.()
    } catch {
      setDraft(body)
      setSendError('Not sent — check the connection and try again.')
    }
  }

  return (
    <div className="bd-chat" role="dialog" aria-label={`Project chat — ${project}`} onPointerDown={(e) => e.stopPropagation()}>
      <div className="bd-chat-head">
        <Icon name="chat" size={16} />
        <span className="bd-chat-title">{project}</span>
        <button type="button" className="bd-chat-x" aria-label="Close the chat" onClick={onClose}>
          <Icon name="close" size={16} />
        </button>
      </div>
      <div
        className="bd-chat-list"
        ref={list}
        onScroll={(e) => {
          const el = e.currentTarget
          atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
        }}
      >
        {hasOlder && (
          <button type="button" className="bd-chat-older" disabled={loadingOlder} onClick={loadOlder}>
            {loadingOlder ? 'Loading…' : 'Earlier messages'}
          </button>
        )}
        {messages.map((m) => (
          <Message
            key={m.message_id}
            m={m}
            selfEmail={who?.email ?? null}
            selfFace={who?.picture ?? null}
            projectId={convId}
            onOpenDoc={openOutside}
          />
        ))}
        {!messages.length && <div className="bd-chat-empty">{loading ? 'Loading the chat…' : 'No messages yet — say something to the project.'}</div>}
      </div>
      <form
        className="bd-chat-compose"
        onSubmit={(e) => {
          e.preventDefault()
          void send()
        }}
      >
        <div className="bd-chat-from">
          <Icon name={'page' in from ? 'description' : 'dashboard'} size={13} />
          <span>
            Sent from <b>{from.name}</b>
          </span>
        </div>
        {quote && (
          <div className="bd-chat-quote">
            <span>{quote}</span>
            <button type="button" aria-label="Remove the quote" title="Remove the quote" onClick={() => onQuoteDone?.()}>
              <Icon name="close" size={14} />
            </button>
          </div>
        )}
        {mention && <AgentMention agents={agents} query={mention.query} onPick={insertMention} onDismiss={() => setMention(null)} />}
        <div className="bd-chat-row">
          <button type="button" className="bd-chat-at" aria-label="Mention an agent" title="Mention an agent (@)" onMouseDown={(e) => e.preventDefault()} onClick={startMention}>
            @
          </button>
          <textarea
            ref={box}
            rows={1}
            value={draft}
            placeholder="Message the project…"
            aria-label="Message the project"
            onChange={(e) => {
              setDraft(e.target.value)
              syncMention(e.target)
            }}
            // The caret moved (arrows, a tap): the `@` under it may have changed. Not onSelect — it also fires on the
            // keydown that picks an agent, before the inserted tag is drawn, and would reopen the list on the old text.
            onKeyUp={(e) => syncMention(e.currentTarget)}
            onClick={(e) => syncMention(e.currentTarget)}
            onKeyDown={(e) => {
              // The @ picker took this key (it listens first, at the document): Enter picked an agent, Escape closed
              // the picker — neither sends nor closes the window.
              if (e.defaultPrevented) return
              if (e.key === 'Escape') {
                e.preventDefault()
                onClose()
                return
              }
              if (isSendKey({ key: e.key, shiftKey: e.shiftKey, isComposing: e.nativeEvent.isComposing, keyCode: e.keyCode })) {
                e.preventDefault()
                void send()
              }
            }}
          />
          <button type="submit" className="bd-chat-send" aria-label="Send" disabled={!draft.trim()}>
            <Icon name="send" size={18} />
          </button>
        </div>
        {sendError && (
          <div className="bd-chat-err" role="alert">
            {sendError}
          </div>
        )}
      </form>
    </div>
  )
}

/**
 * The room's chat over a board or a page: one subscription for the window and its button's count of new messages. What
 * was there when the board or page opened is not news; what the reader has open in front of them is read.
 */
export function useRoomChat(convId: string) {
  const chat = useProjectMessages(convId)
  const [chatOpen, setChatOpen] = useState(false)
  /** The highest message number the reader has had in front of them (null until the chat has loaded once). */
  const [seen, setSeen] = useState<number | null>(null)
  const newest = chat.messages.reduce((n, m) => (typeof m.cursor === 'number' && m.cursor < Number.MAX_SAFE_INTEGER ? Math.max(n, m.cursor) : n), 0)
  useEffect(() => {
    if ((seen === null && !chat.loading) || chatOpen) setSeen((s) => Math.max(s ?? 0, newest))
  }, [chat.loading, chatOpen, newest, seen])
  const unread = chatOpen || seen === null ? 0 : unreadSince(chat.messages, seen)
  return { chat, chatOpen, setChatOpen, unread }
}

/**
 * How many messages came in since the reader last had the chat open — the count on the button. `seen` is the highest
 * message number they have had in front of them; what they typed themselves (or a send still on its way) never
 * counts — but their own agent's answer does: it speaks as them (`mine`), and it is the answer they are waiting for.
 */
export function unreadSince(messages: ProjectMessage[], seen: number): number {
  let n = 0
  for (const m of messages) {
    if (m.status === 'pending' || (m.mine === true && !m.from_ref?.agent)) continue
    if (typeof m.cursor === 'number' && m.cursor > seen && m.cursor < Number.MAX_SAFE_INTEGER) n++
  }
  return n
}
